import { useApolloClient } from "@apollo/client";
import { getDataOrNull, nl2br } from "@msys/common";
import { LabeledValue, Modal, TruncatedHtmlValue } from "@msys/ui";
import { AutoFixHigh as AutoFixHighIcon } from "@mui/icons-material";
import { Box, CircularProgress, InputAdornment, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { RichTextField } from "../../../commons/form-fields/RichTextField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { useHeaderBox_ModifyItemInfoMutation } from "../boxes/HeaderBox.generated.js";
import {
  useAiDescriptionModal_AiQuery,
  useAiDescriptionModal_ItemQuery,
} from "./AiDescriptionModal.generated.js";
import { Locale } from "../../../../clients/graphqlTypes.js";

interface FormValues {
  aiTitle: string;
  aiDescription: string;
  useTitle: boolean;
  useDescription: boolean;
}

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  handleClose(): void;
  refetchQueries?: string[];
}

export const AiDescriptionModal = ({
  projectId,
  docId,
  itemId,
  handleClose,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  const client = useApolloClient();
  const viewer = useUserData().currentUser!;

  const itemQuery = useAiDescriptionModal_ItemQuery({
    client,
    variables: {
      projectId,
      docId,
      itemId,
    },
    fetchPolicy: "no-cache",
  });
  // splitting AI query since it may take way too long time
  const aiQuery = useAiDescriptionModal_AiQuery({
    client,
    variables: {
      projectId,
      docId,
      itemId,
      locale: viewer.locale as Locale,
    },
    fetchPolicy: "no-cache",
  });

  const item = getDataOrNull(itemQuery.data?.item);
  const ai = aiQuery.data?.aiDescription;

  const [modifyItemInfo] = useHeaderBox_ModifyItemInfoMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const initialValues = React.useMemo(
    (): FormValues => ({
      aiTitle: ai?.title ?? "",
      aiDescription: ai?.description ? `<p>${nl2br(ai?.description)}</p>` : "",
      useTitle: true,
      useDescription: true,
    }),
    [ai?.title, ai?.description]
  );

  const handleSubmit = async (values: FormValues) => {
    await modifyItemInfo({
      variables: {
        input: {
          itemId,
          docId,
          projectId,
          values: {
            ...(values.useTitle ? { title: values.aiTitle } : undefined),
            ...(values.useDescription
              ? { description: values.aiDescription }
              : undefined),
          },
        },
      },
    });
    handleClose();
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {formikProps => (
        <Modal
          icon={<AutoFixHighIcon />}
          title={t("Auto fix title/description", { ns: "QuoteItem" })}
          handleClose={handleClose}
          maxWidth="sm"
          isLoading={itemQuery.loading}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Use selected text", { ns: "QuoteItem" }),
              handleClick: () => {},
              buttonProps: {
                type: "submit",
                form: formId,
                loading: formikProps.isSubmitting,
                disabled:
                  itemQuery.loading ||
                  aiQuery.loading ||
                  Boolean(aiQuery.error) ||
                  !formikProps.isValid ||
                  (!formikProps.values.useTitle &&
                    !formikProps.values.useDescription),
              },
            },
          ]}
        >
          {item && (
            <Form id={formId}>
              <Stack direction="column" spacing={2}>
                <Stack direction="column" spacing={1}>
                  <LabeledValue label={t("Current title", { ns: "QuoteItem" })}>
                    {item.title}
                  </LabeledValue>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <TextField
                        name="aiTitle"
                        label={t("AI generated title", { ns: "QuoteItem" })}
                        disabled={formikProps.isSubmitting || aiQuery.loading}
                        slotProps={{
                          input: aiQuery.loading
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      color="primary"
                                      size={20}
                                    />
                                  </InputAdornment>
                                ),
                              }
                            : undefined,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 0,
                        flexShrink: 0,
                        display: "flex",
                      }}
                    >
                      <CheckboxField name="useTitle" />
                    </Box>
                  </Stack>
                </Stack>

                <Stack direction="column" spacing={1}>
                  <LabeledValue
                    label={t("Current description", { ns: "QuoteItem" })}
                  >
                    <TruncatedHtmlValue
                      textProps={{ variant: "body2" }}
                      lines={4}
                      html={item.description}
                      showMoreLabel={t("Show more", { ns: "Global" })}
                      showLessLabel={t("Show less", { ns: "Global" })}
                      notSetLabel={t("Not set", { ns: "Global" })}
                    />
                  </LabeledValue>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <RichTextField
                        label={t("AI generated description", {
                          ns: "QuoteItem",
                        })}
                        name="aiDescription"
                        disabled={formikProps.isSubmitting || aiQuery.loading}
                        slotProps={{
                          input: aiQuery.loading
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      color="primary"
                                      size={20}
                                    />
                                  </InputAdornment>
                                ),
                              }
                            : undefined,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 0,
                        flexShrink: 0,
                        display: "flex",
                      }}
                    >
                      <CheckboxField name="useDescription" />
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </Form>
          )}
        </Modal>
      )}
    </Formik>
  );
};
